import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchJson } from "../lib/api";

/**
 * SiteSettingsContext — fetches /api/site-settings once at boot and exposes
 * every CMS-editable field to the whole tree.
 *
 * Rule: every field returned by /api/site-settings MUST have a public
 * consumer. If we ever add a field here that no component reads, the
 * admin toggle becomes a dead switch (that was Phase 5 regression).
 *
 * Helper `text(key, fallback)` returns the admin-edited value if set and
 * non-empty, otherwise the provided fallback (= the hard-coded copy that
 * was there before Phase 8). This keeps the site strictly identical to
 * before at launch: admin has to opt-in per field to change anything.
 */
const SiteSettingsContext = createContext({ settings: {}, loading: true, refresh: async () => {} });

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await fetchJson("/site-settings");
      setSettings(data || {});
    } catch (_e) {
      setSettings({});
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Dynamic favicon injection (spec: favicon comes from site_settings).
  useEffect(() => {
    if (!settings.favicon_url) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = settings.favicon_url.startsWith("http")
      ? settings.favicon_url
      : settings.favicon_url;
  }, [settings.favicon_url]);

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, refresh: load }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

/** Return an admin-edited copy if set (non-empty string), else the fallback. */
export function useCopy(key, fallback) {
  const { settings } = useSiteSettings();
  const v = settings?.[key];
  if (v === undefined || v === null) return fallback;
  const s = String(v).trim();
  return s.length ? v : fallback;
}
